import { useEffect, useState } from 'react'
import { TextInput } from '@mantine/core'
import { useTranslation } from 'react-i18next'
import { V2Modal, V2ModalActions } from '../v2/V2Modal'

function normalizeWebsite(value) {
  const text = value.trim()
  if (!text) return ''
  const candidate = /^[a-z][a-z0-9+.-]*:\/\//i.test(text) ? text : `https://${text}`
  try {
    const url = new URL(candidate)
    if (!['http:', 'https:'].includes(url.protocol) || !url.hostname.includes('.')) return null
    return url.toString()
  } catch {
    return null
  }
}

function manualId() {
  const random = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
  return `manual:${random}`
}

/**
 * "Add New Advertiser" / "Create New Advertiser" from the not-found state. Ad Studio has no
 * API that creates advertisers, so the entry lives in this ad (campaign metadata) and the
 * user's recent list; generation receives the name and website without a Google place.
 */
export function AddAdvertiserModal({ opened, initialName = '', onClose, onSubmit }) {
  const { t } = useTranslation()
  const [name, setName] = useState('')
  const [website, setWebsite] = useState('')
  const [address, setAddress] = useState('')
  const [errors, setErrors] = useState({})

  useEffect(() => {
    if (!opened) return
    setName(initialName.trim())
    setWebsite('')
    setAddress('')
    setErrors({})
  }, [initialName, opened])

  const submit = (event) => {
    event?.preventDefault()
    const next = {}
    const cleanName = name.trim()
    const cleanWebsite = normalizeWebsite(website)
    if (!cleanName) next.name = t('editorV2.generation.addAdvertiser.nameRequired')
    if (cleanName.length > 255) next.name = t('editorV2.generation.addAdvertiser.nameTooLong')
    if (cleanWebsite === null) next.website = t('editorV2.generation.addAdvertiser.websiteInvalid')
    setErrors(next)
    if (Object.keys(next).length) return
    onSubmit({
      place_id: manualId(),
      name: cleanName,
      website: cleanWebsite || '',
      address: address.trim(),
      manual: true,
    })
  }

  return (
    <V2Modal
      opened={opened}
      onClose={onClose}
      title={t('editorV2.generation.form.addNewAdvertiser')}
      footer={(
        <V2ModalActions
          onCancel={onClose}
          confirmLabel={t('editorV2.generation.addAdvertiser.confirm')}
          confirmType="submit"
          form="studio-add-advertiser"
        />
      )}
    >
      <form id="studio-add-advertiser" onSubmit={submit} noValidate style={{ display: 'contents' }}>
        <TextInput
          label={t('editorV2.generation.addAdvertiser.name')}
          withAsterisk
          placeholder={t('editorV2.generation.addAdvertiser.namePlaceholder')}
          value={name}
          onChange={(event) => setName(event.currentTarget.value)}
          error={errors.name}
          maxLength={255}
          data-autofocus
        />
        <TextInput
          label={t('editorV2.generation.form.website')}
          placeholder={t('editorV2.generation.form.websitePlaceholder')}
          description={t('editorV2.generation.addAdvertiser.websiteHelp')}
          inputWrapperOrder={['label', 'input', 'description', 'error']}
          type="url"
          value={website}
          onChange={(event) => setWebsite(event.currentTarget.value)}
          error={errors.website}
        />
        <TextInput
          label={t('editorV2.generation.addAdvertiser.address')}
          placeholder={t('editorV2.generation.addAdvertiser.addressPlaceholder')}
          value={address}
          onChange={(event) => setAddress(event.currentTarget.value)}
          maxLength={255}
        />
      </form>
    </V2Modal>
  )
}
